/**
 * DADDL disclosure select — used by snippets/daddl-select.liquid.
 * Options are `.daddl-select__option` buttons with [data-value].
 * Optional: [data-label], .fi / [data-daddl-select-option-prefix] for the trigger,
 * [data-daddl-select-filter] search, host [data-value-as-label].
 *
 * Clicks use capture + stopImmediatePropagation so Dawn MenuDrawer does not
 * treat these buttons as hamburger-close controls (it binds every <button>).
 */
class DaddlSelect extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.trigger = this.querySelector('[data-daddl-select-trigger]');
    this.list = this.querySelector('[data-daddl-select-list]');
    this.valueEl = this.querySelector('[data-daddl-select-value]');
    this.prefixEl = this.querySelector('[data-daddl-select-prefix]');
    this.input = this.querySelector('[data-daddl-select-input]');
    this.filter = this.querySelector('[data-daddl-select-filter]');
    if (!this.trigger || !this.list) return;

    this.onTriggerClick = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.toggle();
    };
    this.onDocClick = (event) => {
      if (!this.contains(event.target)) this.close();
    };
    this.onKey = (event) => this.handleKey(event);

    this.trigger.addEventListener('click', this.onTriggerClick, true);
    this.list.addEventListener(
      'click',
      (event) => {
        const option = event.target.closest('.daddl-select__option');
        if (!option || !this.list.contains(option)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.choose(option);
      },
      true
    );

    if (this.filter) {
      this.filter.addEventListener('click', (event) => event.stopPropagation());
      this.filter.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') event.preventDefault();
        event.stopPropagation();
      });
      this.filter.addEventListener('input', () => this.filterOptions());
    }

    this.syncSelected();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick, true);
    document.removeEventListener('keydown', this.onKey);
  }

  options() {
    return Array.from(this.list.querySelectorAll('.daddl-select__option'));
  }

  get open() {
    return this.trigger.getAttribute('aria-expanded') === 'true';
  }

  toggle() {
    if (this.open) this.close();
    else this.openList();
  }

  openList() {
    document.querySelectorAll('daddl-select').forEach((el) => {
      if (el !== this && typeof el.close === 'function') el.close();
    });
    this.list.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.onDocClick, true);
    document.addEventListener('keydown', this.onKey);
    this.list.scrollIntoView({ block: 'nearest' });
    if (this.filter) {
      this.filter.focus();
      return;
    }
    const selected =
      this.list.querySelector('.daddl-select__option[aria-selected="true"]') || this.options()[0];
    selected?.focus();
  }

  close() {
    if (!this.open) return;
    this.list.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.onDocClick, true);
    document.removeEventListener('keydown', this.onKey);
    if (this.filter) {
      this.filter.value = '';
      this.filterOptions();
    }
  }

  optionLabel(option) {
    if (option.hasAttribute('data-label')) return option.getAttribute('data-label');
    if (this.hasAttribute('data-value-as-label')) return option.getAttribute('data-value') || '';
    const named = option.querySelector('[data-daddl-select-option-label]');
    if (named) return named.textContent.trim();
    return option.textContent.trim();
  }

  choose(option, { silent } = {}) {
    const value = option.getAttribute('data-value') || '';
    if (this.input) {
      this.input.value = value;
      if (!silent) this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (this.valueEl) this.valueEl.textContent = this.optionLabel(option);

    const prefixSource =
      option.querySelector('[data-daddl-select-option-prefix]') || option.querySelector('.fi');
    if (this.prefixEl && prefixSource) {
      this.prefixEl.replaceChildren(prefixSource.cloneNode(true));
    }

    this.syncSelected(option);
    this.close();
    if (!silent) this.trigger.focus();
    this.dispatchEvent(
      new CustomEvent('daddl-select:change', { bubbles: true, detail: { value, option } })
    );
  }

  setValue(value) {
    const match = this.options().find((option) => option.getAttribute('data-value') === value);
    if (match) {
      this.choose(match, { silent: true });
      return;
    }
    if (this.input) this.input.value = '';
    if (this.valueEl) {
      this.valueEl.textContent = this.getAttribute('data-placeholder') || '';
    }
    this.syncSelected();
  }

  syncSelected(active) {
    const current = this.input ? this.input.value : '';
    this.options().forEach((option) => {
      const selected = active ? option === active : option.getAttribute('data-value') === current;
      option.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
  }

  filterOptions() {
    if (!this.filter) return;
    const query = this.filter.value
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();

    this.options().forEach((option) => {
      const haystack = `${option.getAttribute('data-label') || ''} ${option.textContent}`
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase();
      option.hidden = query.length > 0 && !haystack.includes(query);
    });
  }

  handleKey(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      this.trigger.focus();
      return;
    }
    if (!this.open || event.target === this.filter) return;
    const visible = this.options().filter((option) => !option.hidden);
    const index = visible.indexOf(document.activeElement);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      visible[Math.min(index + 1, visible.length - 1)]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      visible[Math.max(index - 1, 0)]?.focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      visible[0]?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      visible[visible.length - 1]?.focus();
    } else if (event.key === 'Enter' || event.key === ' ') {
      const option = document.activeElement?.closest?.('.daddl-select__option');
      if (option) {
        event.preventDefault();
        this.choose(option);
      }
    }
  }
}

if (!customElements.get('daddl-select')) {
  customElements.define('daddl-select', DaddlSelect);
}
